<template>
  <section>
    <v-form
      ref="addUserForm"
      v-model="valid"
      autocomplete="off"
      name="new-user-form"
      @input="formStateChanged"
    >
      <v-row v-if="userDialog.type === 'edit'">
        <v-col cols="12" sm="6">
          <NamedValueComponent
            v-if="userDataForm.created"
            :label="$t('userProfile.created')"
            :model="getUserFormattedDateTime(userDataForm.created)"
          />
        </v-col>
        <v-col cols="12" sm="6">
          <NamedValueComponent
            :label="$t('userProfile.lastLogin')"
            :model="getLastLogin(userDataForm)"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-text-field
            v-model.trim="userDataForm.email"
            :rules="[
              (v) => !!v || $t('addUserForm.required.emailRequired'),
              (v) => validateEmail(v) || $t('addUserForm.required.emailValid')
            ]"
            :disabled="disableEmail"
            :label="$t('generic.email')"
            name="new-email"
            autocomplete="new-email"
            data-cc="user-data-dialog-email"
            @keydown.space.prevent
          />
        </v-col>

        <v-col cols="12">
          <v-text-field
            v-model.trim="userDataForm.firstName"
            :rules="getNameRules()"
            :label="$t('generic.firstname')"
            name="new-firstName"
            autocomplete="new-firstName"
            data-cc="user-data-dialog-first-name"
          />
        </v-col>

        <v-col cols="12">
          <v-text-field
            v-model.trim="userDataForm.lastName"
            :rules="getNameRules(false)"
            :label="$t('generic.lastname')"
            name="new-lastName"
            autocomplete="new-lastName"
            data-cc="user-data-dialog-last-name"
          />
        </v-col>

        <v-col cols="12">
          <v-text-field
            v-model.trim="userDataForm.mobilePhone"
            :rules="[(v) => phoneNoFormatCheck(v)]"
            :label="$t('addUserForm.labels.phoneNo')"
            type="tel"
            autocomplete="off"
            data-cc="user-data-dialog-phone"
          />
        </v-col>
        <v-col cols="12">
          <v-text-field
            v-model.trim="userDataForm.landLine"
            :rules="[(v) => phoneNoFormatCheck(v)]"
            :label="$t('generic.landline')"
            type="tel"
            autocomplete="off"
            data-cc="user-data-dialog-landline"
          />
        </v-col>

        <v-col v-if="userDialog.user?.settings?.hasMfaEnabled" cols="12">
          <UserMfaStatusComponent />
        </v-col>
      </v-row>
    </v-form>
  </section>
</template>

<script>
import NamedValueComponent from '@/components/common/NamedValueComponent';
import UserManagementService from '@/services/userManagement/UserManagementService';
import UserMfaStatusComponent from '@/components/common/user/UserMfaStatusComponent.vue';

export default {
  name: 'UserDataDialog',
  components: { NamedValueComponent, UserMfaStatusComponent },
  props: {
    userDialog: {
      type: Object,
      required: false
    }
  },
  data() {
    return {
      disableEmail: false,
      userDialogInitial: {},
      valid: false,
      userDataForm: {
        email: '',
        firstName: '',
        lastName: '',
        mobilePhone: '',
        landLine: ''
      }
    };
  },
  computed: {
    formChanged() {
      const {
        firstName: initFirstName,
        lastName: initLastName,
        mobilePhone: initMobilePhone,
        landLine: initLandline
      } = this.userDialogInitial;
      const { firstName, lastName, mobilePhone, landLine } = this.userDataForm;
      return (
        initFirstName !== firstName ||
        initLastName !== lastName ||
        initMobilePhone !== mobilePhone ||
        initLandline !== landLine
      );
    },
    dialogTitle() {
      if (this.userDialog.user.firstName) {
        return `${this.userDialog.user.firstName} ${this.userDialog.user.lastName}`;
      }
      return this.$t('userManagement.createAccount');
    },
    isSaveButtonDisabled() {
      return !this.valid || !this.formChanged;
    }
  },
  methods: {
    formStateChanged(isValid) {
      if (isValid) this.submitWrapper();
    },
    getUserFormattedDateTime(timestamp) {
      return `${this.$dayjs(timestamp).format(
        this.$userSettings.dateMediumFormat
      )} ${this.$dayjs(timestamp).format(this.$userSettings.timeLongFormat)}`;
    },
    validateEmail(email) {
      return UserManagementService.validateEmail(email);
    },
    getLastLogin(user) {
      if (!user.hasOwnProperty('lastLogin') || !user.lastLogin) {
        return '-';
      }
      return this.getUserFormattedDateTime(user.lastLogin);
    },
    getNameRules(isFirstName = true) {
      const requiredMessage = this.$t(
        `addUserForm.required.${isFirstName ? 'first' : 'last'}name`
      );
      return [
        (v) =>
          (v || '').length <= 40 ||
          this.$t('addUserForm.rules.maxLength', {
            maxLength: 40
          }),
        (v) => (!!v && !(v.replaceAll(' ', '').length < 2)) || requiredMessage
      ];
    },
    phoneNoFormatCheck(phoneNo) {
      if (
        phoneNo &&
        !UserManagementService.hasPhoneNumberValidFormat(phoneNo)
      ) {
        return this.$t('addUserForm.required.phoneNoValid');
      }

      return true;
    },
    submitWrapper() {
      if (this.isSaveButtonDisabled) return;

      Object.keys(this.userDataForm).map((key) => {
        // trim the request
        if (typeof this.userDataForm[key] === 'string') {
          this.userDataForm[key] = this.userDataForm[key].trim();
        }
        // set every request empty string value to null so backend doesn't parse them
        if (this.userDataForm[key] === '') this.userDataForm[key] = null;
      });

      this.$emit('userDataChanged', { ...this.userDataForm });
    }
  },
  watch: {
    isSaveButtonDisabled(value) {
      this.$emit('userDataButtonDisabled', value);
    },
    userDataForm: {
      handler() {
        this.submitWrapper();
      },
      deep: true
    }
  },
  mounted() {
    if (this.userDialog.user?.email) {
      const { user } = this.userDialog;
      this.userDialogInitial = { ...user };
      this.userDataForm = { ...user };
      this.disableEmail = true;
    } else {
      this.$refs.addUserForm.reset();
    }
    this.$emit('userDataButtonDisabled', this.isSaveButtonDisabled);
  }
};
</script>
